import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { Bell, Search, Sun, Moon, Menu, LogOut, User, Settings, Plus, ChevronDown, CircleDot } from 'lucide-react';
import api from '../services/api';

export default function Topbar() {
  const { user, logout, hasPermission } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const profileRef = useRef(null);
  const notifRef = useRef(null);
  const quickRef = useRef(null);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
      if (quickRef.current && !quickRef.current.contains(e.target)) setShowQuickAdd(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await api.get('/notifications', { params: { limit: 5 } });
      if (res.data.success) {
        setNotifications(res.data.data || []);
        setUnread(res.data.unread_count || 0);
      }
    } catch { /* ignore */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const quickActions = [];
  if (hasPermission('create_sales')) {
    quickActions.push({ label: 'Transaksi Penjualan', path: '/sales/new', icon: CircleDot });
  }
  if (hasPermission('create_purchase_orders')) {
    quickActions.push({ label: 'Purchase Order', path: '/purchasing/orders', icon: CircleDot });
  }
  if (hasPermission('create_goods_receipts')) {
    quickActions.push({ label: 'Barang Masuk', path: '/inventory/in', icon: CircleDot });
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-white/70 bg-white/55 px-4 shadow-[0_1px_0_0_rgba(255,255,255,0.6)] backdrop-blur-2xl dark:border-white/10 dark:bg-surface-950/65 lg:px-8">
      {/* Left Side */}
      <div className="flex items-center gap-3">
        <button onClick={toggleSidebar} className="btn-icon lg:hidden" title="Toggle sidebar">
          <Menu size={20} />
        </button>

        {/* Global Search */}
        <div className="hidden w-72 items-center gap-2.5 rounded-full border border-surface-200 bg-surface-50 px-4 py-2 transition-all duration-200 focus-within:border-[#2563eb] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#2563eb] dark:border-surface-800 dark:bg-surface-900 dark:focus-within:border-[#60a5fa] dark:focus-within:bg-surface-900 dark:focus-within:ring-[#60a5fa] md:flex lg:w-96">
          <Search size={15} className="flex-shrink-0 text-surface-400" />
          <input
            type="text"
            placeholder="Cari menu, barang, atau transaksi..."
            className="w-full border-none bg-transparent text-sm text-surface-700 outline-none placeholder-surface-400 dark:text-surface-200"
          />
          <kbd className="hidden flex-shrink-0 rounded-md border border-surface-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-surface-400 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-500 lg:inline-flex">/</kbd>
        </div>
      </div>

      {/* Right Side */}
      <div className="flex items-center gap-1.5">
        {/* Quick Add */}
        {quickActions.length > 0 && (
          <div ref={quickRef} className="relative">
            <button
              onClick={() => setShowQuickAdd(!showQuickAdd)}
              className="btn-primary btn-sm hidden gap-1.5 sm:flex"
            >
              <Plus size={15} />
              <span>Tambah</span>
              <ChevronDown size={13} className={`transition-transform duration-200 ${showQuickAdd ? 'rotate-180' : ''}`} />
            </button>
            {showQuickAdd && (
              <div className="absolute right-0 top-full z-50 mt-2 w-60 animate-slide-up overflow-hidden rounded-2xl border border-white/80 bg-white/85 py-1.5 shadow-dropdown backdrop-blur-2xl dark:border-white/10 dark:bg-surface-900/90">
                <p className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-surface-400">Aksi Cepat</p>
                {quickActions.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => { navigate(action.path); setShowQuickAdd(false); }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-surface-700 transition-colors hover:bg-surface-50 dark:text-surface-200 dark:hover:bg-surface-800"
                  >
                    <div className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Theme toggle */}
        <button onClick={toggleTheme} className="btn-icon" title={isDark ? 'Mode terang' : 'Mode gelap'}>
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notifications */}
        <div ref={notifRef} className="relative">
          <button onClick={() => setShowNotif(!showNotif)} className="btn-icon relative" title="Notifikasi">
            <Bell size={18} />
            {unread > 0 && (
              <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-danger-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center shadow-sm">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
          {showNotif && (
            <div className="absolute right-0 top-full z-50 mt-2 max-h-96 w-80 animate-slide-up overflow-y-auto rounded-2xl border border-white/80 bg-white/85 py-2 shadow-dropdown backdrop-blur-2xl dark:border-white/10 dark:bg-surface-900/90">
              <div className="flex items-center justify-between border-b border-surface-100 px-4 py-2 dark:border-surface-800">
                <h3 className="text-sm font-semibold text-surface-900 dark:text-white">Notifikasi</h3>
                {unread > 0 && (
                  <button onClick={() => navigate('/notifications')} className="text-xs font-medium text-primary-900 hover:text-primary-950 dark:text-primary-400">
                    Lihat Semua
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-surface-400 dark:text-surface-500">
                  <Bell size={24} className="mx-auto mb-2 opacity-40" />
                  Tidak ada notifikasi
                </div>
              ) : (
                <div className="divide-y divide-surface-100 dark:divide-surface-800">
                  {notifications.slice(0, 5).map(n => (
                    <div key={n.id} className={`px-4 py-3 hover:bg-surface-50 dark:hover:bg-surface-800/50 cursor-pointer transition-colors ${!n.is_read ? 'bg-primary-50/30 dark:bg-primary-900/10' : ''}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.is_read ? 'bg-primary-500' : 'bg-transparent'}`} />
                        <div>
                          <p className="text-sm font-medium text-surface-900 dark:text-white">{n.title}</p>
                          <p className="text-xs text-surface-500 mt-0.5">{n.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Profile */}
        <div ref={profileRef} className="relative">
          <button 
            onClick={() => setShowProfile(!showProfile)} 
            className="flex items-center gap-2.5 rounded-full border border-transparent p-1 pl-1 transition-colors hover:border-surface-200 hover:bg-surface-50 dark:hover:border-surface-700 dark:hover:bg-surface-900 lg:pr-3"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 shadow-glow">
              <span className="text-xs font-semibold text-primary-900">{user?.fullName?.charAt(0) || 'U'}</span>
            </div>
            <div className="hidden text-left lg:block">
              <p className="text-[13px] font-medium leading-tight text-surface-900 dark:text-white">{user?.fullName || 'User'}</p>
              <p className="mt-0.5 text-[10px] leading-tight text-surface-400 dark:text-surface-500">{user?.role?.displayName}</p>
            </div>
            <ChevronDown size={13} className={`hidden text-surface-400 transition-transform duration-200 lg:block ${showProfile ? 'rotate-180' : ''}`} />
          </button>
          {showProfile && (
            <div className="absolute right-0 top-full z-50 mt-2 w-56 animate-slide-up overflow-hidden rounded-2xl border border-white/80 bg-white/85 py-1.5 shadow-dropdown backdrop-blur-2xl dark:border-white/10 dark:bg-surface-900/90">
              <div className="px-4 py-2.5 border-b border-surface-100 dark:border-surface-800 mb-1">
                <p className="text-sm font-medium text-surface-900 dark:text-white">{user?.fullName}</p>
                <p className="text-xs text-surface-400">{user?.email || ''}</p>
              </div>
              <button onClick={() => { navigate('/profile'); setShowProfile(false); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                <div className="p-1 rounded-lg bg-surface-100 dark:bg-surface-700"><User size={14} /></div>
                Profil
              </button>
              <button onClick={() => { navigate('/settings'); setShowProfile(false); }} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                <div className="p-1 rounded-lg bg-surface-100 dark:bg-surface-700"><Settings size={14} /></div>
                Pengaturan
              </button>
              <hr className="my-1 border-surface-100 dark:border-surface-800" />
              <button onClick={handleLogout} className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/10 transition-colors">
                <div className="p-1 rounded-lg bg-danger-50 dark:bg-danger-900/20"><LogOut size={14} /></div>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
